/**
 * About screen — hotline, socials, official links, disclaimer.
 */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { getAbout, type AboutData } from "@/api";
import { useI18n } from "@/i18n";
import { openLink } from "@/tg/init";
import BackBar from "@/components/BackBar";

const SOCIAL_ICONS: Record<string, string> = {
  facebook: "f",
  instagram: "ig",
  telegram: "tg",
  twitter: "x",
  x: "x",
  linkedin: "in",
  youtube: "yt",
};

export default function About() {
  const { lang, t } = useI18n();
  const [about, setAbout] = useState<AboutData | null>(null);

  useEffect(() => {
    getAbout(lang).catch(() => null).then((d) => d && setAbout(d));
  }, [lang]);

  // Fallback data for dev/no-backend
  const data: AboutData = about ?? {
    hotline: "+38 044 3900 601",
    emails: ["info@prytulafoundation.org"],
    socials: {
      facebook:  "https://www.facebook.com/prytulafoundation.org",
      instagram: "https://www.instagram.com/prytulafoundation/",
      telegram:  "https://t.me/prytulafoundation",
      x:         "https://x.com/charityprytula",
      linkedin:  "https://www.linkedin.com/company/prytulafoundation/",
      youtube:   "https://www.youtube.com/@prytulafoundation",
    },
    disclaimer: t("disclaimer"),
    official_site: "https://prytulafoundation.org",
    official_channel: "https://t.me/prytulafoundation",
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0, transition: { duration: 0.25 } }}
      exit={{ opacity: 0, x: -24 }}
      className="min-h-screen pb-8"
      style={{ background: "var(--tg-bg)" }}
    >
      <BackBar to="/home" />

      <div className="px-4 pt-2 space-y-6">
        <h1 className="text-xl font-bold" style={{ color: "var(--tg-text)" }}>
          {t("about")}
        </h1>

        {/* Hotline */}
        <section>
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: "var(--tg-hint)" }}>
            Гаряча лінія
          </p>
          <a
            href={`tel:${data.hotline.replace(/\s/g, "")}`}
            className="text-2xl font-bold"
            style={{ color: "var(--brand-blue)", textDecoration: "none" }}
          >
            {data.hotline}
          </a>
        </section>

        {/* Emails */}
        {data.emails.length > 0 && (
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: "var(--tg-hint)" }}>
              Email
            </p>
            {data.emails.map((email) => (
              <a
                key={email}
                href={`mailto:${email}`}
                className="block text-sm"
                style={{ color: "var(--brand-blue)" }}
              >
                {email}
              </a>
            ))}
          </section>
        )}

        {/* Socials */}
        <section>
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "var(--tg-hint)" }}>
            Соцмережі
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.socials).map(([key, url]) => (
              <button
                key={key}
                onClick={() => openLink(url)}
                className="px-3 py-2 rounded-xl text-sm font-semibold capitalize"
                style={{
                  background: "var(--tg-secondary-bg)",
                  color: "var(--tg-text)",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {SOCIAL_ICONS[key.toLowerCase()] ? (
                  <span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                ) : (
                  key
                )}
              </button>
            ))}
          </div>
        </section>

        {/* Official links */}
        <section className="space-y-2">
          <button
            onClick={() => openLink(data.official_site)}
            className="w-full py-3 rounded-xl font-semibold text-sm"
            style={{ background: "var(--brand-blue)", color: "#ffffff", border: "none", cursor: "pointer", borderRadius: "var(--r-pill)" }}
          >
            Офіційний сайт
          </button>
          <button
            onClick={() => openLink(data.official_channel)}
            className="w-full py-3 rounded-xl font-semibold text-sm"
            style={{ background: "var(--tg-secondary-bg)", color: "var(--tg-text)", border: "none", cursor: "pointer" }}
          >
            Telegram-канал
          </button>
        </section>

        {/* Disclaimer */}
        <p className="text-xs leading-relaxed" style={{ color: "var(--tg-hint)" }}>
          {data.disclaimer || t("disclaimer")}
        </p>
      </div>
    </motion.div>
  );
}
